






















































































import { Component, Prop, Vue } from 'vue-property-decorator'

/**
 * Header-Komponente für die Landingpage (Vue 2 + Vuetify + TypeScript).
 * Enthält: animierte Wellen, Smartphone, das auf der Welle reitet,
 * aufsteigende Blasen (per Prop `bubbles` abschaltbar), Logo-Schriftzug, Slogan und Claim. Die Buttons kommen darunter auf die Landingpage.
 *
 * Breakpoints entsprechen Vuetify 2: xs <600, sm 600, md 960, lg 1264, xl 1904.
 */
/** Wellenbreite im SVG-Koordinatensystem (viewBox 0 0 1000 100) */
const WAVE_W = 1000
const WAVE_STEPS = 125

/**
 * Wellenkante als Summe dreier Sinuswellen (zwei laufende Wellen + leichtes Auf und Ab).
 * `k` bestimmt Richtung und Tempo; die hintere Welle läuft mit negativem k gegenläufig.
 */
function waveY (x: number, t: number, k: number): number {
  const u = (x / WAVE_W) * 16.4 - 8.2
  const f =
    Math.sin(u * 0.5 + t * 0.9 * k) * 0.28 +
    Math.sin(u * 0.23 - t * 0.6 * k + 2.4) * 0.22 +
    Math.sin(4.8 + t * 0.7 * k) * 0.1
  return 52 - f * (32 / 0.6)
}

function wavePath (t: number, k: number): string {
  let d = ''
  for (let i = 0; i <= WAVE_STEPS; i++) {
    const x = (i * WAVE_W) / WAVE_STEPS
    d += (i ? 'L' : 'M') + x.toFixed(1) + ' ' + waveY(x, t, k).toFixed(2)
  }
  return d + `L${WAVE_W} 100L0 100Z`
}

/** Feste (deterministische) Blasen-Konfiguration, relativ zur Smartphone-Breite */
const BUBBLES: Array<Record<string, string>> = Array.from({ length: 16 }, (_, i) => {
  const r = (n: number): number => {
    const v = Math.sin((i + 1) * 12.9898 + n * 78.233) * 43758.5453
    return v - Math.floor(v)
  }
  return {
    '--x': (8 + r(1) * 84).toFixed(1) + '%',
    '--y': (18 + r(2) * 34).toFixed(1) + '%',
    '--s': (0.018 + r(3) * 0.026).toFixed(3),
    '--d': (3.2 + r(4) * 2.6).toFixed(1) + 's',
    '--dl': (-r(5) * 6).toFixed(1) + 's',
    '--dx': (-24 + r(6) * 48).toFixed(0) + 'px',
    '--r': (0.35 + r(7) * 0.4).toFixed(2)
  }
})

@Component
export default class L3rnHeroHeader extends Vue {
  /** Beschriftung des Logos für Screenreader (der Schriftzug selbst ist als SVG-Pfad eingebettet) */
  @Prop({ type: String, default: 'L3RN-online' }) readonly title!: string
  @Prop({ type: String, default: 'never stop learning' }) readonly tagline!: string
  @Prop({ type: String, default: 'Jetzt Schulungen selbst erstellen -' }) readonly claimHead!: string
  @Prop({ type: String, default: 'smarter denn je!' }) readonly claimHighlight!: string
  @Prop({
    type: String,
    default:
      'Profitieren Sie von unserer all-in-one Lösung, unserem innovativen Lernmanagementsystem mit KI gestütztem Autorentool.'
  }) readonly claimText!: string
  /** Aufsteigende Blasen um das Smartphone ein-/ausschalten */
  @Prop({ type: Boolean, default: true }) readonly bubbles!: boolean
  /** Slogan-SVG „never stop learning“ (Standard: /L3rnTagline.svg aus dem public-Ordner) */
  @Prop({ type: String, default: '' }) readonly taglineSrc!: string
  @Prop({ type: String, default: '' }) readonly imageSrc!: string
  @Prop({ type: String, default: 'Smartphone, aus dem eine Welle bricht' }) readonly imageAlt!: string

  // Nicht-reaktive Laufzeit-Variablen für die Wellen-Animation
  private rafId = 0
  private t0!: number
  private io!: IntersectionObserver | null

  get bubbleDots (): Array<Record<string, string>> {
    return BUBBLES
  }

  mounted (): void {
    this.t0 = performance.now()
    this.drawWaves(2)
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) return
    this.startWaves()
    if ('IntersectionObserver' in window) {
      // Animation nur laufen lassen, solange der Header sichtbar ist
      this.io = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) this.startWaves()
        else this.stopWaves()
      })
      this.io.observe(this.$el)
    }
  }

  beforeDestroy (): void {
    this.stopWaves()
    if (this.io) this.io.disconnect()
  }

  startWaves (): void {
    if (!this.rafId) this.rafId = requestAnimationFrame(this.onFrame)
  }

  stopWaves (): void {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = 0
  }

  onFrame (now: number): void {
    this.rafId = requestAnimationFrame(this.onFrame)
    this.drawWaves((now - this.t0) / 1000)
  }

  drawWaves (t: number): void {
    const front = this.$refs.waveFront as SVGPathElement | undefined
    const back = this.$refs.waveBack as SVGPathElement | undefined
    if (front) front.setAttribute('d', wavePath(t, 1))
    if (back) back.setAttribute('d', wavePath(t + 3, -1.3))
  }

  get phoneSrc (): string {
    if (this.imageSrc) return this.imageSrc
    return '/L3rnSmartphoneWave.png'
  }

  get taglineUrl (): string {
    return this.taglineSrc || '/L3rnTagline.svg'
  }
}
